import type { NotificationDto } from "../../api";
import { AppRoutes } from "../../common/routes/app_routes";

/**
 * Audience hints used for routes that differ between buyer and seller views.
 * For example, the documents section lives at `/account?tab=documents` for
 * buyers but at `/settings` for sellers.
 */
export interface NotificationViewer {
  isSeller?: boolean;
  isBuyer?: boolean;
}

/**
 * Maps a notification to the SPA route the user should land on when they
 * click it. The FE owns routing — kinds map here so the backend never has to
 * know our URL shape. Newer producers omit `link` entirely and pass the entity
 * id in `payload`; `n.link` remains only as a fallback for older rows.
 *
 * Order of resolution:
 *   1. Kind-specific override (this map) — wins because routes are FE-owned.
 *   2. `notification.link` from the backend — for kinds we haven't taught
 *      the resolver about (most order/document events already pass a sane
 *      link, e.g. "/orders/123").
 *   3. `null` — clicking the row marks it read but does not navigate.
 */
function isLegacyChangeRequestLink(link: string | null | undefined): boolean {
  if (!link) return false;
  return link.startsWith("/change-requests/") || link.startsWith("/admin/change-requests/");
}

function extractChangeRequestIdFromLink(link: string | null | undefined): string | null {
  if (!link) return null;
  const match = link.match(/\/change-requests\/([^/?#]+)/);
  return match ? match[1] : null;
}

export function resolveNotificationLink(
  n: NotificationDto,
  viewer?: NotificationViewer,
): string | null {
  const payload = (n.payload ?? {}) as Record<string, unknown>;

  if (
    payload.entity === "productRequest" &&
    typeof payload.productRequestId === "string" &&
    payload.productRequestId.length > 0
  ) {
    return viewer?.isSeller || viewer?.isBuyer
      ? AppRoutes.PRODUCT_REQUEST_DETAILS.replace(":id", payload.productRequestId)
      : AppRoutes.ADMIN_PRODUCT_REQUEST_DETAILS.replace(":id", payload.productRequestId);
  }

  if (
    payload.entity === "purchaseOrder" &&
    (typeof payload.purchaseOrderId === "string" || typeof payload.purchaseOrderId === "number") &&
    String(payload.purchaseOrderId).length > 0
  ) {
    const poId = String(payload.purchaseOrderId);
    if (viewer?.isBuyer && typeof payload.saleOrderId === "string" && payload.saleOrderId.length > 0) {
      return `${AppRoutes.ORDERS}/${payload.saleOrderId}`;
    }
    if (!viewer?.isSeller && !viewer?.isBuyer) {
      return AppRoutes.ADMIN_PURCHASE_ORDER_DETAILS.replace(":id", poId);
    }
    return AppRoutes.PURCHASE_ORDER_DETAILS.replace(":id", poId);
  }

  if (payload.entity === "changeRequest" || isLegacyChangeRequestLink(n.link)) {
    const id =
      typeof payload.changeRequestId === "string" && payload.changeRequestId.length > 0
        ? payload.changeRequestId
        : extractChangeRequestIdFromLink(n.link);

    // Admins review change requests on a dedicated admin page.
    if (!viewer?.isSeller && !viewer?.isBuyer) {
      return id
        ? AppRoutes.ADMIN_CHANGE_REQUEST_REVIEW.replace(":id", id)
        : AppRoutes.ADMIN_CHANGE_REQUESTS;
    }

    // Buyers always land on the account tab. The detail view renders
    // inline inside the tab when `id` is present.
    if (viewer?.isBuyer) {
      return id
        ? `${AppRoutes.ACCOUNT}?tab=change-requests&id=${encodeURIComponent(id)}`
        : `${AppRoutes.ACCOUNT}?tab=change-requests`;
    }

    // Sellers go to the dedicated CR detail page when we have an id (it
    // hosts the inline edit & resubmit flow). The list lives inside the
    // settings page, so a missing id falls back there.
    return id
      ? AppRoutes.CHANGE_REQUEST_DETAILS.replace(":id", id)
      : `${AppRoutes.SETTINGS}?section=change-requests`;
  }

  if (n.kind === "warrantyStatusUpdated" || n.kind === "warrantyClaimed") {
    const claimId =
      typeof payload.claimId === "string" && payload.claimId.length > 0
        ? payload.claimId
        : null;
    if (viewer?.isBuyer) {
      return claimId
        ? AppRoutes.MY_WARRANTY_CLAIM_DETAILS.replace(":id", claimId)
        : AppRoutes.MY_WARRANTY_CLAIMS;
    }
    if (viewer?.isSeller) {
      return claimId
        ? AppRoutes.WARRANTY_CLAIM_DETAILS.replace(":id", claimId)
        : AppRoutes.WARRANTY_CLAIMS;
    }
    return claimId
      ? AppRoutes.ADMIN_WARRANTY_CLAIM_DETAILS.replace(":id", claimId)
      : AppRoutes.ADMIN_WARRANTY_CLAIMS;
  }

  if (n.kind === "disputeOpened" || n.kind === "disputeStatusUpdated") {
    const disputeId =
      typeof payload.disputeId === "string" && payload.disputeId.length > 0
        ? payload.disputeId
        : null;
    // Buyers have no dispute detail page — they see the dispute inline on the
    // order it was raised against.
    if (viewer?.isBuyer) {
      const saleOrderId = payload.saleOrderId;
      return saleOrderId != null && String(saleOrderId).length > 0
        ? `${AppRoutes.ORDERS}/${String(saleOrderId)}`
        : AppRoutes.ORDERS;
    }
    if (viewer?.isSeller) return null;
    return disputeId
      ? AppRoutes.ADMIN_ORDER_DISPUTE_DETAILS.replace(":id", disputeId)
      : AppRoutes.ADMIN_ORDER_DISPUTES;
  }

  if (n.kind === "deliveryBreached" || n.kind === "deliveryAtRisk") {
    // Delivery alerts are an admin-facing monitoring feature.
    if (viewer?.isBuyer) return AppRoutes.ORDERS;
    if (viewer?.isSeller) return AppRoutes.PURCHASE_ORDERS;
    return AppRoutes.ADMIN_ORDERS;
  }

  switch (n.kind as string) {
    case "userSignedUp":
      // Admin click → review the new user's KYC docs. If the producer included
      // the new user's id we can deep-link to their docs page; otherwise the
      // generic admin documents queue.
      return typeof payload.userId === "string" && payload.userId.length > 0
        ? AppRoutes.ADMIN_DOCUMENTS_USER.replace(":userId", payload.userId)
        : AppRoutes.ADMIN_DOCUMENTS;

    case "documentApproved":
    case "documentRejected":
      // Backend stores `/documents` as a generic placeholder. The actual
      // page differs per audience: sellers see documents inline on the
      // settings page, buyers see them as a tab on the account page.
      return viewer?.isSeller
        ? AppRoutes.SETTINGS
        : `${AppRoutes.ACCOUNT}?tab=documents`;

    case "welcome":
      return "/";

    default:
      return n.link ?? null;
  }
}
